import { useState, useCallback, useEffect } from 'react';

const API_URL = process.env.REACT_APP_API_URL || 'https://leadgen-api.anassk.workers.dev';
const TOKEN_KEY = 'leadgen_jwt';

// Generate a persistent client ID
function getClientId() {
  let clientId = localStorage.getItem('leadgen_client_id');
  if (!clientId) {
    clientId = 'client_' + Math.random().toString(36).substr(2, 9) + Date.now().toString(36);
    localStorage.setItem('leadgen_client_id', clientId);
  }
  return clientId;
}

// JWT token management
function getStoredToken() {
  return localStorage.getItem(TOKEN_KEY);
}

function storeToken(token) {
  localStorage.setItem(TOKEN_KEY, token);
}

function clearToken() {
  localStorage.removeItem(TOKEN_KEY);
}

export function useUsageTracker() {
  const [usage, setUsage] = useState(null);
  const [isLoading, setIsLoading] = useState(true); // Always start loading
  const [showPaywall, setShowPaywall] = useState(false);
  // SECURITY: Never initialize from localStorage - always start as false
  // Only server-verified JWT can set these to true
  const [isAdmin, setIsAdmin] = useState(false);
  const [isPaid, setIsPaid] = useState(false);
  const [authVerified, setAuthVerified] = useState(false); // Track if server verified
  const [user, setUser] = useState(null); // Store user info
  const [paymentLink, setPaymentLink] = useState('');

  const clientId = getClientId();

  // Verify JWT token with server - SOURCE OF TRUTH
  const verifyToken = useCallback(async () => {
    const token = getStoredToken();
    if (!token) {
      return false;
    }

    try {
      const res = await fetch(`${API_URL}/api/verify`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Client-ID': clientId
        }
      });
      const data = await res.json();

      if (data.success && data.valid) {
        setIsAdmin(data.isAdmin === true);
        setIsPaid(data.isPaid === true);
        setUser(data.user);
        setAuthVerified(true);
        return true;
      } else {
        // Token invalid or expired - clear it
        clearToken();
        setIsAdmin(false);
        setIsPaid(false);
        setUser(null);
        setAuthVerified(true);
        return false;
      }
    } catch (e) {
      console.warn('Token verification failed:', e);
      // Network error - clear token for security
      clearToken();
      setIsAdmin(false);
      setIsPaid(false);
      setUser(null);
      setAuthVerified(true);
      return false;
    }
  }, [clientId]);

  // Fetch current usage status
  const fetchStatus = useCallback(async () => {
    setIsLoading(true);
    try {
      // First, try to verify existing token
      const token = getStoredToken();
      if (token) {
        const verified = await verifyToken();
        if (verified) {
          // Token valid - fetch usage with auth
          const res = await fetch(`${API_URL}/api/status`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'X-Client-ID': clientId
            }
          });
          const data = await res.json();
          if (data.success) {
            setUsage(data.usage);
            setPaymentLink(data.paymentLink);
          }
          setIsLoading(false);
          return;
        }
      }

      // No token or invalid - show login
      setIsAdmin(false);
      setIsPaid(false);
      setAuthVerified(true);
      setUsage({
        houseLookups: { used: 0, limit: 3 },
        boundaryDraws: { used: 0, limit: 10 },
        leadsExtracted: { used: 0, limit: 50 }
      });
    } catch (e) {
      console.warn('Failed to fetch status:', e);
      setIsAdmin(false);
      setIsPaid(false);
      setAuthVerified(true);
    } finally {
      setIsLoading(false);
    }
  }, [clientId, verifyToken]);

  // Track an action
  const trackAction = useCallback(async (action, count = 1) => {
    // Admin/paid users bypass tracking
    if (isAdmin || isPaid) return { allowed: true };

    try {
      const res = await fetch(`${API_URL}/api/track`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Client-ID': clientId
        },
        body: JSON.stringify({ action, count })
      });

      const data = await res.json();

      if (data.success) {
        setUsage(data.usage);
        setIsAdmin(data.isAdmin === true);
        setIsPaid(data.isPaid === true);
        return { allowed: true, remaining: data.remaining };
      } else {
        setPaymentLink(data.paymentLink);
        setShowPaywall(true);
        return { allowed: false, message: data.message };
      }
    } catch (e) {
      console.warn('Failed to track action:', e);
      return { allowed: false, message: 'Network error' };
    }
  }, [clientId, isAdmin, isPaid]);

  // Login (email + password) - stores JWT on success
  const login = useCallback(async (email, password) => {
    try {
      const res = await fetch(`${API_URL}/api/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Client-ID': clientId
        },
        body: JSON.stringify({ email, password })
      });

      const data = await res.json();

      if (data.success && data.token) {
        // Store JWT token securely
        storeToken(data.token);
        // Set state from server response
        setIsAdmin(data.isAdmin === true);
        setIsPaid(data.isPaid === true);
        setUser(data.user);
        setShowPaywall(false);
        setAuthVerified(true);
        return { success: true, message: data.message, user: data.user };
      } else {
        return { success: false, message: data.message, pending: data.pending };
      }
    } catch (e) {
      return { success: false, message: 'Connection error' };
    }
  }, [clientId]);

  // Logout - clears JWT token
  const logout = useCallback(() => {
    clearToken();
    setIsAdmin(false);
    setIsPaid(false);
    setUser(null);
    setAuthVerified(true);
    setUsage({
      houseLookups: { used: 0, limit: 3 },
      boundaryDraws: { used: 0, limit: 10 },
      leadsExtracted: { used: 0, limit: 50 }
    });
  }, []);

  // Update user profile (email, name)
  const updateUserProfile = useCallback(async (updates) => {
    const token = getStoredToken();
    if (!token) return { success: false, message: 'Not logged in' };

    try {
      const res = await fetch(`${API_URL}/api/update-profile`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-Client-ID': clientId
        },
        body: JSON.stringify(updates)
      });

      const data = await res.json();
      if (data.success) {
        // Update local user state
        setUser(prev => ({ ...prev, ...updates }));
      }
      return data;
    } catch (e) {
      return { success: false, message: 'Connection error' };
    }
  }, [clientId]);

  // Signup for new users
  const signup = useCallback(async (userData) => {
    try {
      const res = await fetch(`${API_URL}/api/signup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Client-ID': clientId
        },
        body: JSON.stringify(userData)
      });

      const data = await res.json();
      return { success: data.success, message: data.message };
    } catch (e) {
      return { success: false, message: 'Connection error' };
    }
  }, [clientId]);

  // Check if action is allowed without tracking
  const canPerform = useCallback((action) => {
    if (isAdmin || isPaid) return true;
    if (!usage) return false; // Deny if no usage data

    switch (action) {
      case 'houseLookup':
        return usage.houseLookups.used < usage.houseLookups.limit;
      case 'boundaryDraw':
        return usage.boundaryDraws.used < usage.boundaryDraws.limit;
      case 'extractLeads':
        return usage.leadsExtracted.used < usage.leadsExtracted.limit;
      case 'propertyLookup':
        // Property lookups - limit for free users
        return (usage.propertyLookups?.used || 0) < (usage.propertyLookups?.limit || 5);
      default:
        return false;
    }
  }, [usage, isAdmin, isPaid]);

  // Get remaining for action
  const getRemaining = useCallback((action) => {
    if (isAdmin || isPaid) return 999;
    if (!usage) return 0;

    switch (action) {
      case 'houseLookup':
        return Math.max(0, usage.houseLookups.limit - usage.houseLookups.used);
      case 'boundaryDraw':
        return Math.max(0, usage.boundaryDraws.limit - usage.boundaryDraws.used);
      case 'extractLeads':
        return Math.max(0, usage.leadsExtracted.limit - usage.leadsExtracted.used);
      case 'propertyLookup':
        return Math.max(0, (usage.propertyLookups?.limit || 5) - (usage.propertyLookups?.used || 0));
      default:
        return 0;
    }
  }, [usage, isAdmin, isPaid]);

  // Initial fetch - verify auth with server
  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  return {
    usage,
    isLoading,
    isAdmin,
    isPaid,
    authVerified,
    user,
    showPaywall,
    setShowPaywall,
    paymentLink,
    trackAction,
    canPerform,
    getRemaining,
    login,
    logout,
    signup,
    updateUserProfile,
    fetchStatus
  };
}

export default useUsageTracker;
